import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useGoogleLogin } from "@react-oauth/google";
import { setToken } from "../slices/profileSlice";
import { _google_auth } from "../common/axios_services";
import { showErrorNotification } from "../common/utils/notification";
import { navigateByUserRole } from "../common/utils/navigationUtils";
import AppRoutes from "../common/routes";

const SignInFooter = () => (
  <>
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-300"></div>
      </div>
      <div className="relative flex justify-center text-sm">
        <span className="px-2 bg-white text-gray-600">or</span>
      </div>
    </div>
    <div className="text-center">
      <p className="text-gray-600 text-xl">
        Already have an account?{" "}
        <Link className="inner-link font-semibold" to={AppRoutes.signin}>
          Sign in here.
        </Link>
      </p>
    </div>
  </>
);

// Inner component that uses the Google login hook. Rendered only when a Google
// client id is configured, so the GSI library never initializes without one.
const GoogleLoginButton = ({ isSignUp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          },
        );

        const userInfo = await userInfoResponse.json();

        const storedRef = localStorage.getItem("signup_referral_code");

        const response = await _google_auth({
          idToken: tokenResponse.access_token,
          app: "mobile",
          userInfo: userInfo,
          referralCode: storedRef || undefined,
        });

        if (response?.data?.data?.tokens?.accessToken) {
          const accessToken = response.data.data.tokens.accessToken;
          const userData = response.data.data.user;

          dispatch(setToken(accessToken));
          localStorage.setItem("konnect_token", accessToken);
          navigateByUserRole(userData, navigate);
        }
      } catch (err) {
        showErrorNotification(
          err?.response?.data?.message || "Google login failed",
        );
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      showErrorNotification("Google login failed");
    },
  });

  return (
    <div className="mt-6 space-y-3">
      <button
        type="button"
        onClick={login}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 py-6 px-4 border-2 border-gray-300 rounded-lg font-semibold text-xl text-gray-900 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        <span className="inline">
          {loading ? "Signing in..." : "Continue with Google"}
        </span>
      </button>

      {isSignUp && <SignInFooter />}
    </div>
  );
};

const GoogleAuthButton = ({ isSignUp = false }) => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Google sign-in isn't configured (no REACT_APP_GOOGLE_CLIENT_ID): skip the GSI
  // button entirely so it can't throw "Missing required parameter client_id".
  // Keep the "Sign in here" link on the signup page so that flow isn't lost.
  if (!clientId) {
    return isSignUp ? <div className="mt-6">{<SignInFooter />}</div> : null;
  }

  return <GoogleLoginButton isSignUp={isSignUp} />;
};

export default GoogleAuthButton;
